/**
 * WHERE YOU ARE IN THE ROOM, always on screen at the top of the work area:
 *
 *   [←]  Boardrooms / Gut health apps ▾ / Price map / Bearable      [Recent ▾]
 *
 * It replaces three controls that each did part of this in a different place:
 * "← Boardrooms" in the title row, a trail of crumbs that only appeared once
 * you had taken a step, and History as a tab on the right rail.
 *
 * TWO JOBS, KEPT APART. The crumbs say where you are: the room, the table, the
 * item. Back and Recent say where you were. The old trail listed visits in
 * order, so "Price map › Bearable › Cara Care" read as if Cara Care were
 * inside Bearable.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { BoardroomSummary } from '../../types';
import HistoryList from './HistoryPanel';
import type { Visit } from './reports/place';

export interface PathCrumb {
  key: string;
  label: string;
  /** Where the crumb goes; the last crumb (where you are) has none. */
  onClick?: () => void;
}

/** A small menu under its trigger, closed by a click elsewhere or Escape. */
function useDropdown() {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return { open, setOpen, box };
}

const MENU = 'absolute left-0 top-full z-30 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800';
const SEP = <span aria-hidden className="text-gray-300 dark:text-gray-600">/</span>;

/** The room's own crumb: its name, with a menu to switch to another room. */
function RoomSwitcher({
  roomId,
  roomName,
  current,
  loadRooms,
}: {
  roomId: number;
  roomName: string;
  /** True when the room itself is where you are (nothing open below it). */
  current: boolean;
  loadRooms: (q: string) => Promise<BoardroomSummary[]>;
}) {
  const { open, setOpen, box } = useDropdown();
  const [q, setQ] = useState('');
  const [rooms, setRooms] = useState<BoardroomSummary[] | null>(null);
  useEffect(() => {
    if (!open) return;
    let live = true;
    const load = () =>
      loadRooms(q.trim())
        .then(r => live && setRooms(r))
        .catch(() => live && setRooms([]));
    // Typing waits a moment between keys; opening the menu loads at once.
    const t = q ? window.setTimeout(load, 200) : (load(), undefined);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [open, q, loadRooms]);
  return (
    <div ref={box} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Switch to another recon"
        className={`flex min-w-0 items-center gap-1 rounded px-1 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 ${
          current ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-300'
        }`}
        data-testid="room-switcher"
      >
        <span className="truncate max-w-[16rem]">{roomName}</span>
        <span aria-hidden className="text-[11px] text-gray-400">▾</span>
      </button>
      {open && (
        <div className={`${MENU} w-72 p-2`} role="menu">
          <input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Find a room…"
            className="mb-1 w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-900"
          />
          <ul className="max-h-72 overflow-y-auto text-xs">
            {rooms === null && <li className="px-2 py-1 text-gray-400">Loading…</li>}
            {rooms?.length === 0 && <li className="px-2 py-1 text-gray-400">No rooms match.</li>}
            {rooms?.map(r => (
              <li key={r.id}>
                <Link
                  to={`/boardroom/${r.id}`}
                  onClick={() => setOpen(false)}
                  role="menuitem"
                  aria-current={r.id === roomId ? 'page' : undefined}
                  className={`block truncate rounded px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    r.id === roomId ? 'font-semibold text-indigo-700 dark:text-indigo-300' : 'text-gray-800 dark:text-gray-100'
                  }`}
                  title={r.description}
                >
                  {r.name}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-1 border-t border-gray-100 pt-1 dark:border-gray-700">
            <Link to="/boardroom" onClick={() => setOpen(false)} className="block px-2 py-1 text-xs text-indigo-600 hover:underline dark:text-indigo-400">
              All recons →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LocationBar({
  roomId,
  roomName,
  path,
  back,
  onBack,
  visits,
  onPickVisit,
  onClearVisits,
  loadRooms,
  extra,
}: {
  roomId: number;
  roomName: string;
  /** What is open inside the room, outermost first: a table, then an item. */
  path: PathCrumb[];
  /** Where Back goes, or null when there is nowhere to go back to. */
  back: string | null;
  onBack: () => void;
  visits: Visit[];
  onPickVisit: (v: Visit) => void;
  onClearVisits: () => void;
  loadRooms: (q: string) => Promise<BoardroomSummary[]>;
  /** Anything else the row should carry at its right end. */
  extra?: ReactNode;
}) {
  const recent = useDropdown();
  return (
    <nav aria-label="Where you are" className="flex min-w-0 items-center gap-2 text-sm" data-testid="location-bar">
      <button
        type="button"
        onClick={onBack}
        disabled={!back}
        title={back ? `Back to ${back}` : 'Nowhere to go back to yet'}
        aria-label={back ? `Back to ${back}` : 'Back'}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
        data-testid="location-back"
      >
        ←
      </button>
      <ol className="flex min-w-0 flex-1 items-center gap-1">
        <li className="shrink-0">
          <Link to="/boardroom" className="rounded px-1 py-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100">
            Recons
          </Link>
        </li>
        <li className="flex min-w-0 items-center gap-1">
          {SEP}
          <RoomSwitcher roomId={roomId} roomName={roomName} current={path.length === 0} loadRooms={loadRooms} />
        </li>
        {path.map((c, i) => {
          const here = i === path.length - 1;
          return (
            <li key={c.key} className="flex min-w-0 items-center gap-1">
              {SEP}
              {here || !c.onClick ? (
                <span
                  aria-current={here ? 'page' : undefined}
                  className={`truncate max-w-[18rem] px-1 ${here ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-300'}`}
                  title={c.label}
                >
                  {c.label}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={c.onClick}
                  className="truncate max-w-[14rem] rounded px-1 py-0.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                  title={c.label}
                >
                  {c.label}
                </button>
              )}
            </li>
          );
        })}
      </ol>
      {extra}
      <div ref={recent.box} className="relative shrink-0">
        <button
          type="button"
          onClick={() => recent.setOpen(o => !o)}
          aria-haspopup="menu"
          aria-expanded={recent.open}
          title="Everything you have opened in this room"
          className="rounded-md border border-gray-200 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
          data-testid="location-recent"
        >
          Recent ▾
        </button>
        {recent.open && (
          <div className={`${MENU} left-auto right-0 max-h-96 w-80 overflow-y-auto`}>
            <HistoryList
              visits={visits}
              onPick={v => {
                recent.setOpen(false);
                onPickVisit(v);
              }}
              onClear={onClearVisits}
            />
          </div>
        )}
      </div>
    </nav>
  );
}
